// FolderEntity.jsx
// Display folder and file entities in a tree format.

// Copyright HS Analysis GmbH, 2022
// Authors: Nina Sebold,  Martin Wagner, Jakob Burghardt, Viktor Eberhardt

// Framework imports
import React, { useEffect, useState, useContext } from "react";
import PropTypes from "prop-types";

// External packages
import TreeItem from "@mui/lab/TreeItem";
import { Tooltip } from "@mui/material";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

// HSA Packages
import { FileViewerContext } from "../FileViewer";
import Backend from "../../common/utils/Backend";
import FileFormatUtils from "../../common/utils/FileFormatUtils";

/**
 * Collected (reusable) styles for page elements.
 */
const styles = {
  root: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    width: "300px",
    overflow: "hidden",
  },
  treeItem: {
    width: "100%",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
};

/**
 * Folder of foldertree.
 * @param {string} path Path of folder to get files and folders from. Defaults to "".
 * @returns React render component.
 */
export default function RecursiveFolderEntity({ entity, parent }) {
  const [dirContent, setDirContent] = useState([]);
  const FV = useContext(FileViewerContext);

  useEffect(() => {
    if (entity.type === "file") return;

    let isMounted = true;
    Backend.walkDir(entity.path, true).then((content) => {
      if (!isMounted) return;
      const filteredContent = FileFormatUtils.filterViewerEntities(content);
      setDirContent(filteredContent);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const sortedEntities = React.useMemo(() => {
    const entities = FileFormatUtils.sortEntitiesByTypeAndName(
      dirContent,
      FV.filePreviewSortBy,
      FV.folderSortReverse,
      FV.filePreviewSortReverse
    );
    return entities;
  }, [
    FV.filePreviewSortBy,
    FV.folderSortReverse,
    FV.filePreviewSortReverse,
    dirContent,
  ]);

  return entity.type === "directory" ? (
    <TreeItem
      nodeId={entity.path}
      label={<FolderEntity entity={entity} />}
      onClick={() => FV.setSelectedFolder(entity)}
    >
      {sortedEntities.map((item) => (
        <RecursiveFolderEntity key={item.path} entity={item} parent={entity} />
      ))}
    </TreeItem>
  ) : (
    <TreeItem
      key={entity.path}
      nodeId={entity.path}
      label={<FolderEntity entity={entity} />}
      onClick={() => {
        FV.setActiveFilePath(entity.path);
        FV.setSelectedFolder(parent);
        Backend.getFileIdData(entity.path).then((item) => {
          if (item.sceneIds && item.sceneIds.length > 0) {
            const fileUuid = item.sceneIds[0].id;
            FV.setActiveFileId(fileUuid);
          } else {
            console.error("Invalid or empty sceneIds:", item.sceneIds);
          }
        });
      }}
    />
  );
}

RecursiveFolderEntity.propTypes = {
  entity: PropTypes.object.isRequired,
  parent: PropTypes.object,
};

/**
 *styling of treeitems
 */
export function FolderEntity({ entity }) {
  const path = entity.path;
  const endOfPath = path.split(/[/\\]+/).pop();

  return (
    <div
      style={{
        ...styles.root,
      }}
    >
      {entity.type === "directory" ? (
        <FolderOpenOutlinedIcon sx={{ paddingRight: "5px" }} />
      ) : (
        <InsertDriveFileOutlinedIcon sx={{ paddingRight: "5px" }} />
      )}
      <Tooltip
        title={endOfPath}
        styles={{ width: "100%", textAlign: "center" }}
      >
        <div style={styles.treeItem}>{endOfPath}</div>
      </Tooltip>
    </div>
  );
}

FolderEntity.propTypes = {
  entity: PropTypes.object.isRequired,
};
